/**
 * Dates as people type them.
 *
 * Pickup Appt is typed rather than picked (Alan, 2026-09-26: "pickup appt需要改成可以
 * 直接填写不是直接选项"): the appointment arrives as "9/28 14:00" in an email or a
 * terminal's page, and the native picker made that five clicks and a scroll. The
 * box takes what people write - 9/28 14:00, 9/28/2026 2:30pm, 2026-09-28 14:00,
 * 10月3日 9:00 - and a date with no year is the one nearest today.
 *
 * Bulk paste reads its dates here too, so a value pasted and a value typed mean
 * the same thing.
 */

// The fields edited as typed text rather than through the native picker. Only
// Pickup Appt: the other dates keep the calendar (Alan, 2026-09-26: "这个不改了").
export const TYPED_DATE_TIME_FIELDS = new Set(['pickup_appointment_at']);
export const TYPED_DATE_TIME_PLACEHOLDER = '9/28 14:00  (时间可空 time optional)';

const LA_DAY = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/Los_Angeles', year: 'numeric', month: '2-digit', day: '2-digit',
});

const clean = (value) => String(value ?? '').replace(/ /g, ' ').trim();

function validDay(year, month, day) {
  const millis = Date.UTC(year, month - 1, day, 12);
  const date = new Date(millis);
  return date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 && date.getUTCDate() === day
    ? `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}` : null;
}

/**
 * A typed date as YYYY-MM-DD, or null. Takes 2026-09-28, 2026/9/28, 9/28/2026,
 * 9/28/26, 9月28日 and 9/28. With no year, the year is the one that puts the date
 * nearest today, so "1/5" typed in late September is next January, not last.
 */
export function parseTypedDay(raw, now = Date.now()) {
  const text = clean(raw);
  let match = /^(\d{4})\s*[-/.年]\s*(\d{1,2})\s*[-/.月]\s*(\d{1,2})\s*日?$/u.exec(text);
  if (match) return validDay(Number(match[1]), Number(match[2]), Number(match[3]));
  match = /^(\d{1,2})\s*[-/.月]\s*(\d{1,2})\s*日?(?:\s*[-/.]\s*(\d{4}|\d{2}))?$/u.exec(text);
  if (!match) return null;
  const month = Number(match[1]);
  const day = Number(match[2]);
  if (match[3]) {
    const year = Number(match[3]);
    return validDay(year < 100 ? 2000 + year : year, month, day);
  }
  const thisYear = Number(LA_DAY.format(new Date(now)).slice(0, 4));
  let best = null;
  for (const year of [thisYear - 1, thisYear, thisYear + 1]) {
    const iso = validDay(year, month, day);
    if (!iso) continue;
    const distance = Math.abs(Date.parse(`${iso}T12:00:00Z`) - now);
    if (!best || distance < best.distance) best = { iso, distance };
  }
  return best?.iso ?? null;
}

function parseTime(raw) {
  const text = clean(raw).toLowerCase().replace(/\s+/g, '');
  let match = /^(\d{1,2}):(\d{2})(am|pm)?$/u.exec(text) ?? /^(\d{2})(\d{2})(am|pm)?$/u.exec(text);
  let hour;
  let minute;
  let meridiem;
  if (match) {
    [, hour, minute, meridiem] = match;
  } else {
    match = /^(\d{1,2})(am|pm)$/u.exec(text);
    if (!match) return null;
    [, hour, meridiem] = match;
    minute = '0';
  }
  let hours = Number(hour);
  const minutes = Number(minute);
  if (meridiem) {
    if (hours < 1 || hours > 12) return null;
    if (meridiem === 'pm' && hours < 12) hours += 12;
    if (meridiem === 'am' && hours === 12) hours = 0;
  }
  if (hours > 23 || minutes > 59) return null;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

/**
 * A typed date and time as a local "YYYY-MM-DDTHH:MM", or null. The time may be 14:00,
 * 2:30pm, 2pm or 1430. It is required unless `timeOptional` is set - and Pickup Appt sets
 * it (Alan, 2026-10-10: "pickup appt 有的时候也没有时间所以这个时间可以空着"): a bare date
 * is then the day at local midnight, which the table reads back as "a date, no time".
 */
export function parseTypedDateTime(raw, now = Date.now(), { timeOptional = false } = {}) {
  const text = clean(raw);
  if (timeOptional) {
    const bareDay = parseTypedDay(text, now);
    if (bareDay) return `${bareDay}T00:00`;
  }
  const iso = /^(\d{4}-\d{2}-\d{2})[T\s]+(\S+)$/u.exec(text);
  const split = iso ?? /^(.+?)\s+(\S+(?:\s*[ap]m)?)$/iu.exec(text);
  if (!split) return null;
  const day = iso ? validDay(...split[1].split('-').map(Number)) : parseTypedDay(split[1], now);
  const time = parseTime(split[2]);
  return day && time ? `${day}T${time}` : null;
}

/**
 * An instant that is exactly local midnight is a date with no time - what a bare typed date
 * stores (see parseTypedDateTime). Seconds count: only a clean 00:00:00 is "no time".
 */
export function isLocalMidnight(value) {
  const date = value instanceof Date ? value : value === null || value === undefined || value === '' ? null : new Date(value);
  if (!date || !Number.isFinite(date.getTime())) return false;
  return date.getHours() === 0 && date.getMinutes() === 0 && date.getSeconds() === 0 && date.getMilliseconds() === 0;
}

/**
 * A stored instant as the box shows it: 09/28/2026 14:00, in the browser's own time. With
 * `omitMidnight` a date with no time shows as the date alone, so it can be saved again as it was.
 */
export function formatTypedDateTime(value, { omitMidnight = false } = {}) {
  const date = value instanceof Date ? value : value === null || value === undefined || value === '' ? null : new Date(value);
  if (!date || !Number.isFinite(date.getTime())) return '';
  const two = (number) => String(number).padStart(2, '0');
  const day = `${two(date.getMonth() + 1)}/${two(date.getDate())}/${date.getFullYear()}`;
  return omitMidnight && isLocalMidnight(date) ? day : `${day} ${two(date.getHours())}:${two(date.getMinutes())}`;
}
